import { Component, Input, ViewChild, NgZone, ChangeDetectorRef, TemplateRef, Output, EventEmitter, ViewEncapsulation, AfterViewInit, ViewContainerRef, HostListener } from '@angular/core';
import { TimeoutQueue } from '@common/services/timeout-queue';
import { DataStore } from '@common/data-models/data-store/data-store';
import { TopicRecord } from '@playbook/data-models/records/topic-record';
import { AssignmentRecord } from '@playbook/data-models/records/assignment-record';
import { AppService } from '@suite/app.service';
import { PlaybookService } from '@playbook/services/playbook-service';
import { Loader } from '@loader/loader';
import { Subscription } from 'rxjs'
import { PSTWEB_LIST_VALUE, PST_PREFIX } from '@common/enums/constants';
import { ListData } from '@common/data-models/service/list-data';
import { StTextArea } from '@common/controls/st-text-area/st-text-area';
import { LanguageSettings } from '@common/services/language-settings';
import { ModalService } from '@common/components/modal/modal.service';

import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { NavigationService } from "@common/services/navigation-service";
import { ExternalActionsService } from "@common/services/external-actions-service";
import { LayoutState } from "@common/services/layout-state";
import { SingleTopicViewStateRecord } from "@common/data-models/records/single-topic-viewstate-record";
import { BasicContentService } from "@common/services/basic-content-service";
import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { MatDialog } from '@angular/material/dialog';
import { FieldDef } from "@common/data-models/service/field-def";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { AttachmentService } from '@common/services/attachment-service';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { GroupKey } from '@suite/common/data-models/search-results/group-key';
import { EnpsRecordControl } from '@common/controls/enps-record-control/enps-record-control';
import moment from 'moment-timezone';
import { DraggableEnpsComponent } from '@suite/common/components/base/draggable-enps-component';
import { DateRange } from '../../data-models/complex-types/date-range';
import { Address } from '../../data-models/complex-types/address';
import { FieldDefValidations } from '../../enums/enums';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { MultiValue } from '../../data-models/complex-types/multi-value';
import { HighlightPipe } from '../../pipes/highlight';


@Component({
    selector: 'st-topic-item',
    templateUrl: './st-topic-item.component.html',
    styleUrls: ['./st-topic-item.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        'class': 'st-topic-item'
    }
})
export class StTopicItem extends DraggableEnpsComponent implements AfterViewInit {
    public readonly FieldDefValidations = FieldDefValidations;
    topicEditable: boolean = true;
    assignmentsReadable: boolean = true;
    shouldFocusSlugTextArea: boolean = false;
    pstFieldNames = PSTFieldNames;
    isReadOnlyGrouping: boolean = false;
    isExternalActionPanelOpen = false;
    isAssignmentsPanelOpen = false;
    @ViewChild('slugTextArea') slugTextArea: StTextArea;
    attachments: any;
    recordChangedSubscription: Subscription = null;

    private _data: TopicRecord = null;
    @Input()
    public get data(): TopicRecord {
        return this._data;
    }
    public set data(v: TopicRecord) {
        this._data = v;
        this._guid = v.guid;
        if (v) {
            this.topicEditable = this._data?.getSecurityPrivsWrite(this.appService);
            this.assignmentsReadable = this.data?.getSecurityPrivsReadChildren(this.appService);
            this.isReadOnly = this.viewStateService.inSlideshowMode
            if (this.data && this.data?.path && this.data?.path.isWasteBinPath) {
                this.topicEditable = false;
                this.isReadOnly = true;
            }
            if (this.recordChangedSubscription) this.recordChangedSubscription.unsubscribe();
            this.recordChangedSubscription = super.createSubscription(this._data?.changeSubject, this.recordChanged.bind(this))
        }
    }

    public inView: boolean = false;

    private _isReadonly: boolean = false;
    @Input()
    public get isReadOnly(): boolean {
        return this._isReadonly;
    }
    public set isReadOnly(v: boolean) {
        if (!v && this.topicEditable) {
            this._isReadonly = v;
        } else if (v) {
            this._isReadonly = v;
        }
    }

    @Input() public preview: boolean = false;

    public get customCardItems(): FieldDef[] {
        if (this._customCardItems?.length > 0 && this.preview) {
            return this._customCardItems;
        }
        return this.viewStateService.viewStateCustomCardItems
    }

    private _customCardItems: FieldDef[];
    @Input()
    public set customCardItems(v: FieldDef[]) {
        this._customCardItems = v;
    }

    @Input() public index: number;
    @Input() public loader: Loader;
    @Input() public isActive: boolean = true;
    @Input() public isNew: boolean = false;
    @Input() public currentGroup: GroupKey;

    @Output() public onDrop: EventEmitter<any> = new EventEmitter();
    @Output() public onTopicOpen: EventEmitter<void> = new EventEmitter();
    @Output() public onAssignmentOpen: EventEmitter<AssignmentRecord> = new EventEmitter();

    private _isPublication: boolean = false;
    @Input()
    public get isPublication(): boolean {
        return this._isPublication;
    }
    public set isPublication(v: boolean) {
        this._isPublication = v;
    }
    @Output() public onDeletePublication: EventEmitter<any> = new EventEmitter();

    @ViewChild('externalActions', { read: TemplateRef }) externalActions: TemplateRef<any>;
    @ViewChild('assignmentsOverlay', { read: TemplateRef }) assignmentsOverlay: TemplateRef<any>;
    @ViewChild('assignmentsOverlayEl', { read: CdkConnectedOverlay }) assignmentsOverlayEl: CdkConnectedOverlay;
    @ViewChild('externalActionsEl', { read: CdkConnectedOverlay }) externalActionsEl: CdkConnectedOverlay;

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent) {
        if (this.visualStateService.isLessThanMedium) {
            this.onTopicOpen.emit();
        }
    }

    public get topicTypes(): Array<string> {
        return this.data?.topicType.sort();
    }

    public get topicDepartments(): Array<string> {
        return this.data?.department;
    }

    get timeUpdatedLabel(): string {
        return `${this.languageSettings.PSTWebChangedAgoLabel} ${this.data.modTime?.moment.fromNow()}`;
    }

    get updatedBy(): string {
        return `${this.languageSettings.PSTWebChangedByLabel} ${this.globalTablesService.Staff[this?.data?.modBy]?.DisplayName}`;
    }

    get childrenCount(): number {
        return this.data?.children.length;
    }

    get extraAssignmentsCount(): number {
        return this.data?.children?.length - 4;
    }

    get shouldShowExtraAssignments(): boolean {
        return this.data?.children?.length > 5;
    }


    get assignmentTypes(): { id: string, title: string }[] {
        return this.globalTablesService.getListItemsForField(this.appService.PSTFieldNames.PSTAssignmentCoverageType)
    }

    get topicTypeTranslationLabelPrefix() {
        return `${PSTWEB_LIST_VALUE}_${PSTFieldNames.PSTTopicType}`;
    }

    get showwFullCard(): boolean {
        return !this.visualStateService.isLessThanMedium;
    }

    get maxTagsToShow(): number {
        if (this.visualStateService.isLessThanLarge) {
            return 4;
        } else if (this.visualStateService.isLessThanXLarge) {
            return 6;
        } else {
            if (this.viewStateService.NavigationViewStateRecord.viewStateFilterState && this.appService.sideBarExpanded) {
                return 6;
            } else if (this.viewStateService.NavigationViewStateRecord.viewStateFilterState || this.appService.sideBarExpanded) {
                return 9;
            } else {
                return 12;
            }
        }
    }

    get isFilterOpened(): boolean {
        return this.viewStateService?.NavigationViewStateRecord?.viewStateFilterState;
    }

    get viewStateLayout() {
        return this.viewStateService?.viewStateLayout?.Value
    }

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    get cardDisabled(): boolean {
        return this.isReadOnly || !this.topicEditable || this.preview;
    }


    get searchString(): string {
        return this.viewStateService.viewStateSearchQuery;
    }

    get maxTypesToShow(): number {
        if (this.visualStateService.isLessThanLarge) {
            return 1;
        } else if (this.visualStateService.isLessThanXLarge) {
            return 3;
        } else {
            return 3;
        }
    }

    get enforceRequiredFieldsOnCreate(): boolean {
        return this.globalTablesService.EnforceRequiredFieldsOnCreate
    }

    constructor(
        public appService: AppService,
        public basicContentService: BasicContentService,
        public nomWebServicesJson: NomWebServicesJson,
        public playbookService: PlaybookService,
        public ngZone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
        timeoutQueue: TimeoutQueue,
        public languageSettings: LanguageSettings,
        public modalService: ModalService,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public navigationService: NavigationService,
        public externalActionsService: ExternalActionsService,
        public viewStateService: LayoutState,
        protected dialog: MatDialog,
        protected attachmentService: AttachmentService,
        private vref: ViewContainerRef,
        private highlightPipe: HighlightPipe

    ) {
        super(timeoutQueue);
    }

    recordChanged() {
        this.topicEditable = this.data?.getSecurityPrivsWrite(this.appService);
        this.assignmentsReadable = this.data?.getSecurityPrivsReadChildren(this.appService);
        this.isReadOnly = this.viewStateService.inSlideshowMode; // TODO - speak with Yanko about this
        if (this.data?.path?.isWasteBinPath) {
            this.topicEditable = false;
            this.isReadOnly = true;
        }
        this.itemStringCache.clear();
    }

    async ngOnInit() {
        setTimeout(() => {
            this.inView = true;
        }, 0);

        setTimeout(() => {
            if(this.assignmentsOverlay) {
                this.vref.createEmbeddedView(this.assignmentsOverlay);
            }
        }, 0);
        setTimeout(() => {
            if (this.externalActions) {
                this.vref.createEmbeddedView(this.externalActions);
            }
        }, 0);
        this.isReadOnlyGrouping = this.checkIsReadOnlyGrouping();
    }

    async ngAfterViewInit() {
        setTimeout(() => {
        }, 0)
    }

    dragStartHandler(event: DragEvent) {
        event.dataTransfer.setData('text/plain', PST_PREFIX + JSON.stringify(this.data?.getDataTransferString(this.appService)));
        event.stopPropagation();
    }

    async onENPSObjectDrop(data: any) {
        if (data instanceof ListData) {
            try {
                let res = await this.playbookService.copyScriptToCollection(data, this.data, this.referalId);
                if (res && res.status) {
                    this.data = DataStore.byGuid<TopicRecord>(this.data?.guid, this.referalId);
                    this.data?.addChildren(res.data);
                    if (!this.visualStateService.isMobile) {
                        this.changeDetectorRef.detectChanges();
                    }
                }
            } catch (ex) { }
        }
    }

    async onItemDrop(data: any) {
        if (data instanceof FileList) {
            let attachments = [];
            for (let i = 0; i < data?.length; i++) {
                const shouldProceed = await this.appService.shouldProceedAttachment(data[i].name, data[i].size);
                if (!shouldProceed) return;
                let attachment = data[i];
                attachments.push({ file: attachment, finished: false, name: attachment.name });
            }
            let base64Files = await this.appService.readUrl(attachments);
            for (let file of base64Files) {
                try {
                    file.ExternalFiles = this.data?.attachments;
                    let res = await this.appService.attachmentService.createAttachment(this.data?.database, this.data?.path.fullPath, this.data?.guid, false, file, this.referalId)
                    if (res && res.status) {
                        let data = {
                            "ExternalFiles": this.data?.attachments,
                            "Type": this.data?.type
                        };
                        let updateRes = await this.appService.basicContentService.updateBasicContent<TopicRecord | AssignmentRecord>(this.data?.database, this.data?.path.fullPath, this.data?.guid, false, data, false, null);
                    }
                } catch (ex) { }
            }
        }
        // if (data instanceof DropEvent) {
        //     this.onDrop.emit({ sourceGuid: data?.dragData?.guid, targetGuid: this.data?.guid, sourceGroup: data?.dragData?.group, targetGroup: this.currentGroup });
        // }
    }

    getInnerProp(item: FieldDef) {
        const val = this.data?.get(item.ID.split('-')[0]);
        if (val) return val[item.ComplexPropInnerId]; else return '';
    }

    checkIsReadOnlyGrouping(): boolean {
        if (this.appService.applicationName == 'playbook') {
            let viewStateRec = <SingleTopicViewStateRecord>this.viewStateService.ViewStateRecord;
            if (viewStateRec && viewStateRec.viewStateGrouping) {
                let groupByField = viewStateRec.viewStateGrouping.Value
                return groupByField && this.globalTablesService.FieldDefsDict[groupByField] && this.globalTablesService.FieldDefsDict[groupByField].ReadOnlyValue
            }
        }
        return false;
    }

    public getDateTimeValue(id: string): DateTime {
        return this.data.getAs<DateTime>(id, new DateTime());
    }

    // TODO: Talk with Yanko for this, not sure why
    /*
    getDateTimeValue(id: string): string {
        const value = this.data?.getAs<DateTime>(id, new DateTime());
        if (value) {
            if (value instanceof DateTime && value.get()) {
                if (value.get() && value.get().getUTCFullYear() != 1800) {
                    return DateTime.formatDate(DateTime.convertToTargetTZ(value.get(), this.globalTablesService.ClientTimeZone.id, ((this.globalTablesService.PersonalTimeZone) ? this.globalTablesService.PersonalTimeZone : this.globalTablesService.ClientTimeZone).id))
                } else {
                    return '';
                }
            }
            return DateTime.formatDate(new Date());
        } else {
            return '';
        }
    }
    */

    getTimeControlValue(id: string): string {
        const value = this.data?.getAs<DateTime>(id, new DateTime());
        let dateVal = new Date();
        if (!value) return '';
        if (value instanceof DateTime) {
            if (value.get() && value.get().getUTCFullYear() != 1800) {
                dateVal = DateTime.convertToTargetTZ(value.get(), this.globalTablesService.ClientTimeZone.id, ((this.globalTablesService.PersonalTimeZone) ? this.globalTablesService.PersonalTimeZone : this.globalTablesService.ClientTimeZone).id);
            } else {
                dateVal = null;
            }
        }

        if (dateVal) {
            let minutes = dateVal.getMinutes();
            let minutesString = (minutes < 10) ? "0" + minutes : minutes;
            let hours = dateVal.getHours();
            let hoursString = (hours < 10) ? "0" + hours : hours;
            if (dateVal.getUTCFullYear() != 1800) {
                let time = hoursString.toString() + ":" + minutesString.toString();
                return moment(time, 'LT').format('LT');
            }
        }
        return '';
    }

    async createAssignment(type: string) {
        if (this.isReadOnly) return;
        let path = this.data.path.fullPath + '\\R_' + this.data.guid;
                let database = this.data.database;
        try {
                let res = await this.playbookService.createNewAssignment(database, path, type, this.data, this.referalId);
                if (res && res.status) {
                    this.data = DataStore.byGuid<TopicRecord>(this.data.guid, this.referalId);
                    if (!this.data.hasChildWithGuid(res.data.guid)) {
                        this.data.addChildren(res.data);
                    }
                    if (!this.visualStateService.isMobile) {
                        let updateRes = await this.appService.basicContentService.updateBasicContent<TopicRecord | AssignmentRecord>(this.data.database, this.data.path.fullPath, this.data.guid, false, { "Type": this.data.type }, false, null);
                    }
            }
        } catch (ex) {
            console.log(ex);
        }
    }

    updateTopic(value: any, fieldId: string) {
        if (this.isReadOnly) return;
        if (fieldId == PSTFieldNames.PSTTopicSlug) {
            this.data.topicSlug = value.trim();
        }
        if (fieldId == PSTFieldNames.PSTTopicEventStartDate) {
            const castedVal = value as DateTime;
            if (castedVal?.get() > this.data.getAs<DateTime>(PSTFieldNames.PSTTopicEventEndDate)?.get()) {
                EnpsRecordControl.saveChanges(new DateTime(castedVal?.get()), PSTFieldNames.PSTTopicEventEndDate, this.data);
            }
        }
        EnpsRecordControl.saveChanges(value, fieldId, this.data);
    }

    getDateRangeValue(id: string): DateRange {
        return this.data.getAs<DateRange>(id, new DateRange());
    }

    updateTopicAddresSubField(value: any, field: FieldDef, subFieldId: string) {
        if (this.isReadOnly) return;
        const fieldId = field?.ID?.split('-')?.[0];
        if (fieldId) {
            const field = this.data?.fieldDefsDict[fieldId];
            if (field?.Validation.toUpperCase() === FieldDefValidations.ADDRESS) {
                const address = this.data?.getAs<Address>(fieldId, new Address());
                address[subFieldId] = value;
                this.updateTopic(address, fieldId);
            }
        }
    }

    onMouseOutAssignments(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-assignments-overlay')) {
            this.isAssignmentsPanelOpen = false;
        }
    }

    assignmentsPanelAttached() {
        this.panelAttached(this.assignmentsOverlayEl);
    }

    private panelAttached(overlayEl: CdkConnectedOverlay = null) {
        const overlayElement = overlayEl?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener("mouseleave", (e) => {
                this.isExternalActionPanelOpen = false;
                this.isAssignmentsPanelOpen = false;
            });
        }
    }

    openAssignmentsOverlay() {
        if (this.cardDisabled) return;
        this.isAssignmentsPanelOpen = true;
    }


    //TODO: Probably needs to me util function
    public itemStringCache = new Map<string, string>();
    getItemString(itemID: string): string {
        const item = this.globalTablesService.FieldDefsDict[itemID];
        if (this.itemStringCache.has(item?.ID)) {
            return this.itemStringCache.get(item?.ID);
        }
        let str = "";
        const itemData = this.data.get(item?.ID, null);
        switch (item?.Validation?.toUpperCase()) {
            case FieldDefValidations.CHECKBOX: {
                str = itemData ? this.appService.languageSettings.get('PSTWebYesOption') : this.appService.languageSettings.get('PSTWebNoOption');
                break;
            }
            case FieldDefValidations.CHECKLISTEDIT:
            case FieldDefValidations.CHECKLIST: {
                if (itemData) {
                    let options = this.globalTablesService.getListItemsForField(item?.ID);
                    if (itemID == PSTFieldNames.PSTTopicDepartment) {
                        options = this.globalTablesService.getListItemsForDesk(this.data.type);
                    }
                    str = (itemData as MultiValue<string>).map((item) => {
                        return options.find((option) => option.id === item)?.title;
                    }).join(', ');
                }
                break;
            }
            case FieldDefValidations.STAFFIDLIST: {
                if (itemData) {
                    str = (itemData as MultiValue<string>).map((item) => {
                        return this.globalTablesService.Staff[item]?.DisplayName;
                    }).join(', ');
                }
                break;
            }
            case FieldDefValidations.COMBOBOX: {
                const options = this.globalTablesService.getListItemsForField(item?.ID);
                str = options.find((option) => option.id === itemData)?.title || '';
                break;
            }
            case FieldDefValidations.TIME: {
                if (itemData) {
                    str = (itemData as DateTime)?.toDateTimeString();
                }
                break;
            }
            case FieldDefValidations.DATE: {
                if (itemData) {
                    str = (itemData as DateTime)?.toDateString();
                }
                break;
            }
            case FieldDefValidations.TEXT: {
                if (itemData) {
                    str = (itemData as String)?.toString();
                    if (str) {
                        this.highlightPipe.transform(str, this.searchString, false);
                    }
                }
                break;
            }
            default: {
                str = itemData?.toString() || '';
                break;
            }
        }
        this.itemStringCache.set(item?.ID, str);
        return str;
    }

    public getMinDate(id: string): DateTime {
        if(id == PSTFieldNames.PSTTopicEventEndDate) {
            return this.data.getAs<DateTime>(PSTFieldNames.PSTTopicEventStartDate);
        }
    }

    focusDuplicate() {
        this.onTopicOpen.emit();
    }

    public deletePublication(event: any) {
        event.stopPropagation();
        this.onDeletePublication.emit();
    }
}
